import React, { useMemo } from 'react';
import { Plus, Minus } from 'lucide-react';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { parseChainSteps, stringifyChainSteps } from './approbationsShared';
import { buildApprobationsEmployeeSelectOptions } from './approbationsListPageUtils';

const EMPTY_STEP = { step: 1, employee_id: '', employee_name: '', role: '' };

/**
 * Visual editor for approval chain steps — writes canonical JSON to chain_json / workflow_json.
 */
export default function ApprobationsChainStepsField({
  value,
  onChange,
  employeeOptions = [],
  addStepLabel = 'إضافة خطوة',
  rolePlaceholder = 'الدور (اختياري)',
  employeePlaceholder = 'المعتمد',
}) {
  const steps = useMemo(() => {
    const parsed = parseChainSteps(value);
    return parsed.length ? parsed : [{ ...EMPTY_STEP }];
  }, [value]);

  const employeeSelectOptions = useMemo(
    () => buildApprobationsEmployeeSelectOptions(employeeOptions, { emptyLabel: employeePlaceholder }),
    [employeeOptions, employeePlaceholder],
  );

  const commit = (nextSteps) => {
    onChange(stringifyChainSteps(nextSteps));
  };

  const updateStep = (index, patch) => {
    const next = steps.map((s, i) => (i === index ? { ...s, ...patch, step: i + 1 } : { ...s, step: i + 1 }));
    commit(next);
  };

  const addStep = () => {
    commit([...steps, { ...EMPTY_STEP, step: steps.length + 1 }]);
  };

  const removeStep = (index) => {
    if (steps.length <= 1) {
      commit([{ ...EMPTY_STEP }]);
      return;
    }
    commit(steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, step: i + 1 })));
  };

  const onEmployeeChange = (index, empValue) => {
    const opt = employeeSelectOptions.find((o) => String(o.value) === String(empValue));
    updateStep(index, {
      employee_id: empValue || '',
      employee_name: opt?.meta?.name || '',
    });
  };

  return (
    <div className="space-y-3">
      {steps.map((step, index) => (
        <div
          key={`chain-step-${index}`}
          className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto] gap-2 items-end rounded-xl border border-white/15 bg-white/5 p-3"
          data-testid={`approbations-chain-step-${index}`}
        >
          <span className="text-white/60 text-sm font-medium px-1 pb-2">{index + 1}</span>
          <div className="min-w-0">
            <CustomSelect
              dataTestId={`approbations-chain-employee-${index}`}
              value={step.employee_id ? String(step.employee_id) : ''}
              onChange={(v) => onEmployeeChange(index, v)}
              options={employeeSelectOptions}
              searchable
              creatable={false}
              placeholder={employeePlaceholder}
            />
          </div>
          <div className="min-w-0">
            <input
              type="text"
              value={step.role || ''}
              onChange={(e) => updateStep(index, { role: e.target.value })}
              placeholder={rolePlaceholder}
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-700/50"
              data-testid={`approbations-chain-role-${index}`}
            />
          </div>
          <button
            type="button"
            onClick={() => removeStep(index)}
            className="p-2.5 rounded-xl border border-amber-800/40 bg-amber-950/30 text-amber-200 hover:bg-amber-900/40 transition-colors shrink-0"
            title="حذف الخطوة"
            data-testid={`approbations-chain-remove-${index}`}
          >
            <Minus size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addStep}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-emerald-800/40 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/40 text-sm font-medium transition-colors"
        data-testid="approbations-chain-add-step"
      >
        <Plus size={16} />
        {addStepLabel}
      </button>
    </div>
  );
}
